import { isNonEmptyString, isPlainObject } from '../../model/utility';
import { DashboardLayouts } from '../layout/dashboard-layouts';
import {
  DASHBOARD_VIEWS_VERSION,
  DashboardSavedViews,
  DashboardView,
  MAX_SAVED_VIEWS,
  viewNameProblem,
} from './dashboard-view';

export interface DashboardViewsParseResult {
  /** null when the document is unusable as a whole */
  views: DashboardSavedViews | null;
  warnings: string[];
}

/** Turns an untrusted saved-views document into valid views, dropping views that are malformed or unusable; never throws. */
export function parseDashboardViews(raw: unknown, layouts: DashboardLayouts): DashboardViewsParseResult {
  const warnings: string[] = [];
  if (!isPlainObject(raw) || raw['version'] !== DASHBOARD_VIEWS_VERSION || !Array.isArray(raw['views'])) {
    warnings.push(`Saved views are not a version ${DASHBOARD_VIEWS_VERSION} views document`);
    return { views: null, warnings };
  }

  const views: DashboardView[] = [];

  raw['views'].forEach((entry, index) => {
    const view = parseView(entry, views, layouts, warnings);
    if (!view) {
      warnings.push(`View at index ${index} is unusable and was dropped`);
    } else if (views.length >= MAX_SAVED_VIEWS) {
      warnings.push(`View "${view.name}" exceeds the ${MAX_SAVED_VIEWS} saved views allowed and was dropped`);
    } else {
      views.push(view);
    }
  });

  const activeViewId = raw['activeViewId'];
  const active = views.some((view) => view.id === activeViewId) ? (activeViewId as string) : null;
  if (active === null && activeViewId != null) {
    warnings.push(`Active view ${JSON.stringify(activeViewId)} is not a saved view; opening the default`);
  }

  return { views: { version: DASHBOARD_VIEWS_VERSION, activeViewId: active, views }, warnings };
}

function parseView(
  raw: unknown,
  kept: readonly DashboardView[],
  layouts: DashboardLayouts,
  warnings: string[],
): DashboardView | null {
  if (!isPlainObject(raw) || !isNonEmptyString(raw['id']) || typeof raw['name'] !== 'string') {
    return null;
  }

  const { id, name } = raw;
  if (kept.some((view) => view.id === id)) {
    warnings.push(`View id "${id}" is duplicated`);
    return null;
  }

  const nameProblem = viewNameProblem(
    name,
    kept.map((view) => view.name),
  );
  if (nameProblem) {
    warnings.push(`View "${name}": the name is ${nameProblem}`);
    return null;
  }

  const { layout, warnings: layoutWarnings } = layouts.parse(raw['layout']);
  warnings.push(...layoutWarnings.map((warning) => `View "${name}": ${warning}`));

  return layout && { id, name: name.trim(), layout };
}
